import React, { useState, useEffect } from "react";
import { Card, Col, Row, Typography, Space } from "antd";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ShoppingCartOutlined,
  UserOutlined,
  AppstoreOutlined,
  TruckOutlined,
  OrderedListOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import BASEURL from "../API/Config";

const { Title, Text } = Typography;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCustomers: 0,
    totalCategories: 0,
    totalOrders: 0,
    deliveriesToday: 0,
    canceledOrders: 0,
    outForDelivery: 0,
    storeName: "Shakambari Ecommerce",
    storeAddress: "Madurai, Tamil Nadu, India",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token"); 

        const ordersRes = await axios.get(`${BASEURL}/user/getallorders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const orders = ordersRes.data;

        const totalOrders = orders.length;
        const deliveriesToday = orders.filter((o) => o.orderStatus === "Delivered").length;
        const canceledOrders = orders.filter((o) => o.orderStatus === "cancelled").length;
        const outForDelivery = orders.filter((o) => o.orderStatus === "Out for Delivery").length;

        const productsRes = await axios.get(`${BASEURL}/Products`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const totalProducts = productsRes.data.products?.length || 0;

        const customersRes = await axios.get(`${BASEURL}/user/all-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const totalCustomers = customersRes.data.length || 0;

        setStats((prev) => ({
          ...prev,
          totalProducts,
          totalCustomers,
          totalCategories: 12,
          totalOrders,
          deliveriesToday,
          canceledOrders,
          outForDelivery,
        }));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };
    fetchData();
  }, []);

  const cardData = [
    {
      title: "Total Products",
      value: stats.totalProducts,
      icon: <ShoppingCartOutlined />,
      color: "#4f46e5",
      trend: "+12% from last month"
    },
    {
      title: "Active Customers",
      value: stats.totalCustomers,
      icon: <UserOutlined />,
      color: "#10b981",
      trend: "+5% new users today"
    },
    {
      title: "Categories",
      value: stats.totalCategories,
      icon: <AppstoreOutlined />,
      color: "#f59e0b",
      trend: "Across all departments"
    },
    {
      title: "Total Orders",
      value: stats.totalOrders,
      icon: <OrderedListOutlined />,
      color: "#8b5cf6",
      trend: "Recent orders included"
    },
    {
      title: "Delivered",
      value: stats.deliveriesToday,
      icon: <TruckOutlined />,
      color: "#0ea5e9",
      trend: "Successfully completed"
    },
    {
      title: "Cancelled",
      value: stats.canceledOrders,
      icon: <CloseCircleOutlined />,
      color: "#ef4444",
      trend: "Requires attention"
    },
    {
      title: "Out for Delivery",
      value: stats.outForDelivery,
      icon: <ClockCircleOutlined />,
      color: "#ec4899",
      trend: "In progress"
    },
  ];

  return (
    <div className="dashboard-wrapper">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="d-flex justify-content-between align-items-end">
            <div>
              <Text type="secondary" style={{ fontSize: '14px', fontWeight: 500 }}>OVERVIEW</Text>
              <Title level={2} style={{ margin: 0, marginTop: '4px' }}>Dashboard Overview</Title>
            </div>
            <Text type="secondary">{new Date().toDateString()}</Text>
          </div>

          <Row gutter={[24, 24]}>
            {cardData.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card 
                    className="glass-card" 
                    bordered={false}
                    style={{ overflow: 'hidden' }}
                  >
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div 
                        style={{ 
                          padding: '12px', 
                          borderRadius: '12px', 
                          background: `${card.color}15`,
                          color: card.color,
                          fontSize: '24px',
                          display: 'flex'
                        }}
                      >
                        {card.icon}
                      </div>
                    </div>
                    <div>
                      <Title level={3} style={{ marginBottom: '4px', marginTop: 0 }}>
                        <CountUp end={card.value} duration={2} separator="," />
                      </Title>
                      <Text strong style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{card.title}</Text>
                      <div style={{ marginTop: '12px', fontSize: '12px', color: '#10b981' }}>
                        {card.trend}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Card className="glass-card" bordered={false} style={{ marginTop: '16px' }}>
              <Row gutter={24} align="middle">
                <Col>
                  <div style={{ 
                    width: '64px', 
                    height: '64px', 
                    borderRadius: '16px', 
                    background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '24px'
                  }}>
                    <ShopOutlined />
                  </div>
                </Col>
                <Col>
                  <Title level={4} style={{ margin: 0 }}>{stats.storeName}</Title>
                  <Text type="secondary">{stats.storeAddress}</Text>
                </Col>
              </Row>
            </Card>
          </motion.div>
        </Space>
      </motion.div>

      <style jsx="true">{`
        .dashboard-wrapper {
          padding-bottom: 2rem;
        }
        .ant-typography h2 {
          font-family: 'Outfit', sans-serif !important;
          font-weight: 700 !important;
        }
        .glass-card {
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;

